import React, { useState } from 'react';
import { Descriptions } from 'antd';
import { IconConfirmDelete } from '@nxsys/assets/icons';
import { DocumentType } from '@nxsys/types/models/document';
import Icon from '@nxsys/components/AppIconImage';
import {
  IconEdit,
  IconDelete,
  IconDownload,
} from '@nxsys/components/AppIconSvg';
import AppAttachment from '@nxsys/components/AppAttachment';
import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppConfirmationModal from '@nxsys/components/AppConfirmationModal';
import { useIsAllowedDocumentCreate } from '@nxsys/hooks/useEmployeePermission';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';

type DetailOtherModalProps = {
  isOpen: boolean;
  title: string;
  documentSelected: DocumentType | null;
  toggle: () => void;
  toggleUpdate: (documentSelected?: DocumentType) => void;
  onDeleteDocument: (id: string, cb?: () => void) => void;
};

const DetailOtherModal: React.FC<DetailOtherModalProps> = ({
  title,
  isOpen,
  documentSelected,
  toggle,
  toggleUpdate,
  onDeleteDocument,
}: DetailOtherModalProps) => {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const canCreateDocument = useIsAllowedDocumentCreate();
  const { isBureau } = useSelector((state: AppState) => state.appStore);
  const onUpdate = () => {
    if (!documentSelected?.id) return;
    toggle();
    toggleUpdate(documentSelected);
  };

  const onDelete = () => {
    setDeleteModalOpen(!isDeleteModalOpen);
  };

  const onConfirm = () => {
    if (documentSelected) {
      onDeleteDocument(documentSelected.id, () => {
        onDelete();
        toggle();
      });
    }
  };

  const subcontractorRef = documentSelected?.subcontractorRef;

  const downloadAction = {
    name: 'Download',
    variant: 'outlined' as const,
    buttonType: 'primary' as const,
    icon: <IconDownload width={20} height={20} />,
    onClick: () => {
      if (documentSelected?.documentFile) {
        window.open(documentSelected.documentFile, '_blank');
      }
    },
  };

  const actions = canCreateDocument
    ? [
        {
          name: 'Delete',
          variant: 'outlined' as const,
          buttonType: 'primary' as const,
          className: 'btn-delete',
          icon: <IconDelete width={20} height={20} />,
          onClick: onDelete,
        },
        downloadAction,
        {
          name: 'Edit',
          buttonType: 'primary' as const,
          icon: <IconEdit width={20} height={20} color="#FFFFFF" />,
          onClick: onUpdate,
        },
      ]
    : [downloadAction];

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={actions}
    >
      <AppDescription
        column={1}
        labelStyle={{ width: 216 }}
        bordered={false}
        colon={false}
        size="middle"
      >
        <Descriptions.Item label="Document ID">
          {documentSelected?.id || '-'}
        </Descriptions.Item>
        {!isBureau && (
          <Descriptions.Item label="Agency Ref">
            {subcontractorRef
              ? `${subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
                  subcontractorRef?.agencyRef ||
                  subcontractorRef?.id ||
                  '_'
                }`
              : '-'}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Subcontrator & Employee">
          {subcontractorRef
            ? `${subcontractorRef?.forename} ${subcontractorRef?.forename}`
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Document Title">
          {documentSelected?.documentTitle || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Document Type">
          {documentSelected?.documentType || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Attachment">
          {documentSelected?.documentFile ? (
            <a
              href={documentSelected.documentFile}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <AppAttachment
                urlFile={documentSelected.documentFile}
                title={documentSelected.documentName}
                className="attachment-box"
              />
            </a>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Document Date">
          {documentSelected?.documentDate
            ? new Date(documentSelected.documentDate).toLocaleDateString()
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Expiry Date">
          {documentSelected?.expiryDate
            ? new Date(documentSelected.expiryDate).toLocaleDateString()
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Uploaded By">
          {documentSelected?.updatedBy
            ? `${documentSelected?.updatedBy?.forename} ${documentSelected?.updatedBy?.surname}`
            : '-'}
        </Descriptions.Item>
      </AppDescription>
      <AppConfirmationModal
        open={isDeleteModalOpen}
        title="Delete Document"
        icon={<Icon src={IconConfirmDelete} alt="Delete" />}
        paragraph={
          <p>
            Are you sure want to delete <a>{documentSelected?.id || ''}</a>{' '}
            document?
          </p>
        }
        onDeny={onDelete}
        onConfirm={onConfirm}
      />
    </AppModalContainer>
  );
};

export default DetailOtherModal;
